/**
 * API `ValidateCerca` / ValidaTexto(cod, …, tipo 0) allows only `^[a-zA-Z0-9_-]{1,20}$` (no spaces).
 */
export function sanitizeCercaCodForInsert(cod) {
  return String(cod || '')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_-]/g, '')
    .substring(0, 20);
}

/**
 * Build MerusTracker route shape JSON from a GeoJSON LineString feature.
 */
export function lineStringFeatureToMerusShape(feature) {
  if (!feature || !feature.geometry || feature.geometry.type !== 'LineString') {
    return null;
  }
  const coordinates = feature.geometry.coordinates;
  if (!Array.isArray(coordinates) || coordinates.length === 0) {
    return null;
  }
  return JSON.stringify(
    coordinates.map((c) => ({ lt: Number(c[1]), lg: Number(c[0]) })),
  );
}

/**
 * Outer ring of polygon -> Merus shape [{ lt, lg }, ...]
 */
export function polygonFeatureToMerusShape(feature) {
  if (!feature || !feature.geometry || feature.geometry.type !== 'Polygon') {
    return null;
  }
  const ring = feature.geometry.coordinates && feature.geometry.coordinates[0];
  if (!Array.isArray(ring) || ring.length < 3) {
    return null;
  }
  return JSON.stringify(
    ring.map(([lng, lat]) => ({ lt: Number(lat), lg: Number(lng) })),
  );
}

function escapeXml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function lineStringCoordinatesToKml(coords) {
  if (!Array.isArray(coords) || coords.length === 0) {
    return '';
  }
  return coords
    .map((c) => `${Number(c[0])},${Number(c[1])},0`)
    .join(' ');
}

function parseMaybeJson(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
}

/**
 * Approximate a circle (lat/lng + radius meters) as a GeoJSON Polygon Feature.
 */
export function circleToPolygonFeature(lat, lng, radiusM, properties = {}, segments = 64) {
  const latN = Number(lat);
  const lngN = Number(lng);
  const r = Number(radiusM);
  if (!Number.isFinite(latN) || !Number.isFinite(lngN) || !Number.isFinite(r) || r <= 0) {
    return null;
  }
  const n = Math.max(8, Math.min(128, Number(segments) || 64));
  const latRad = (latN * Math.PI) / 180;
  const metersPerDegLat = 111320;
  const metersPerDegLng = Math.max(1e-9, 111320 * Math.cos(latRad));
  const ring = [];
  for (let i = 0; i <= n; i++) {
    const a = (i / n) * 2 * Math.PI;
    ring.push([
      lngN + (r * Math.cos(a)) / metersPerDegLng,
      latN + (r * Math.sin(a)) / metersPerDegLat,
    ]);
  }
  return {
    type: 'Feature',
    properties: properties || {},
    geometry: { type: 'Polygon', coordinates: [ring] },
  };
}

/**
 * Normalize raw GeoJSON (Feature, FeatureCollection, or Geometry) into a Feature.
 */
export function normalizeGeoJsonFeature(raw) {
  const parsed = parseMaybeJson(raw);
  if (!parsed || typeof parsed !== 'object') return null;

  if (parsed.type === 'Feature' && parsed.geometry) {
    return {
      type: 'Feature',
      properties: parsed.properties && typeof parsed.properties === 'object' ? { ...parsed.properties } : {},
      geometry: parsed.geometry,
    };
  }

  if (parsed.type === 'FeatureCollection' && Array.isArray(parsed.features)) {
    const first = parsed.features.find((f) => f && f.geometry);
    if (!first) return null;
    return {
      type: 'Feature',
      properties: first.properties && typeof first.properties === 'object' ? { ...first.properties } : {},
      geometry: first.geometry,
    };
  }

  if (parsed.type && parsed.coordinates) {
    return {
      type: 'Feature',
      properties: {},
      geometry: parsed,
    };
  }

  return null;
}

/**
 * Convert a MerusTracker cerca (fence) into a GeoJSON Feature (Polygon or Point).
 */
export function cercaToGeoJsonFeature(cerca, nameFallback = 'Cerca') {
  if (!cerca || typeof cerca !== 'object') return null;

  const lat =
    cerca.lat_centro != null
      ? Number(cerca.lat_centro)
      : cerca.latitude != null
        ? Number(cerca.latitude)
        : cerca.lat != null
          ? Number(cerca.lat)
          : null;
  const lng =
    cerca.lng_centro != null
      ? Number(cerca.lng_centro)
      : cerca.longitude != null
        ? Number(cerca.longitude)
        : cerca.lng != null
          ? Number(cerca.lng)
          : null;
  const name =
    String(cerca.nome_cerca || cerca.endereco || nameFallback || 'Cerca').trim() || nameFallback || 'Cerca';
  const props = {
    name,
    featureType: 'fence',
    id_cerca: cerca.id_cerca != null ? cerca.id_cerca : null,
    cod_cerca: cerca.cod_cerca != null ? cerca.cod_cerca : null,
  };

  const isCircular = Number(cerca.is_circular) === 1;
  const radiusM = Number(cerca.raio_mts);
  // Cercas circulares (ex.: CD) devem ir como Point: o assistente de linhas
  // casa Points com o catálogo circular; Polygon só casa cercas não-circulares.
  if (isCircular && Number.isFinite(lat) && Number.isFinite(lng)) {
    return {
      type: 'Feature',
      properties: {
        ...props,
        is_circular: 1,
        raio_mts: Number.isFinite(radiusM) && radiusM > 0 ? radiusM : 75,
      },
      geometry: { type: 'Point', coordinates: [lng, lat] },
    };
  }

  const fromGeojson = normalizeGeoJsonFeature(cerca.geojson);
  if (fromGeojson && fromGeojson.geometry) {
    if (fromGeojson.geometry.type === 'Polygon' || fromGeojson.geometry.type === 'MultiPolygon') {
      return {
        ...fromGeojson,
        properties: { ...(fromGeojson.properties || {}), ...props },
      };
    }
    if (fromGeojson.geometry.type !== 'Point') {
      return {
        ...fromGeojson,
        properties: { ...(fromGeojson.properties || {}), ...props },
      };
    }
  }

  let pointLat = lat;
  let pointLng = lng;
  if (
    (!Number.isFinite(pointLat) || !Number.isFinite(pointLng)) &&
    fromGeojson &&
    fromGeojson.geometry &&
    fromGeojson.geometry.type === 'Point' &&
    Array.isArray(fromGeojson.geometry.coordinates)
  ) {
    pointLng = Number(fromGeojson.geometry.coordinates[0]);
    pointLat = Number(fromGeojson.geometry.coordinates[1]);
  }

  if (Number.isFinite(pointLat) && Number.isFinite(pointLng)) {
    return {
      type: 'Feature',
      properties: props,
      geometry: { type: 'Point', coordinates: [pointLng, pointLat] },
    };
  }

  return null;
}

function geometryToKmlPlacemark(feature, defaultName) {
  if (!feature || !feature.geometry || !feature.geometry.type) return '';
  const name = escapeXml((feature.properties && feature.properties.name) || defaultName);
  const type = feature.geometry.type;
  const coords = feature.geometry.coordinates;

  if (type === 'LineString') {
    const line = lineStringCoordinatesToKml(coords);
    if (!line) return '';
    return `<Placemark><name>${name}</name><LineString><coordinates>${line}</coordinates></LineString></Placemark>`;
  }

  if (type === 'Point' && Array.isArray(coords) && coords.length >= 2) {
    return `<Placemark><name>${name}</name><Point><coordinates>${Number(coords[0])},${Number(coords[1])},0</coordinates></Point></Placemark>`;
  }

  if (type === 'Polygon' && Array.isArray(coords) && Array.isArray(coords[0])) {
    const ring = lineStringCoordinatesToKml(coords[0]);
    if (!ring) return '';
    return `<Placemark><name>${name}</name><Polygon><outerBoundaryIs><LinearRing><coordinates>${ring}</coordinates></LinearRing></outerBoundaryIs></Polygon></Placemark>`;
  }

  if (type === 'MultiPolygon' && Array.isArray(coords)) {
    const polygons = coords
      .map((poly) => {
        if (!Array.isArray(poly) || !Array.isArray(poly[0])) return '';
        const ring = lineStringCoordinatesToKml(poly[0]);
        if (!ring) return '';
        return `<Polygon><outerBoundaryIs><LinearRing><coordinates>${ring}</coordinates></LinearRing></outerBoundaryIs></Polygon>`;
      })
      .filter(Boolean)
      .join('');
    if (!polygons) return '';
    return `<Placemark><name>${name}</name><MultiGeometry>${polygons}</MultiGeometry></Placemark>`;
  }

  return '';
}

/**
 * GeoJSON FeatureCollection (LineString / Polygon / Point) -> KML Document XML string.
 */
export function featureCollectionToKml(featureCollection) {
  const features = (featureCollection && featureCollection.features) || [];
  const placemarks = features
    .map((f) => geometryToKmlPlacemark(f, 'Rota'))
    .filter(Boolean)
    .join('');
  return (
    '<?xml version="1.0" encoding="UTF-8"?>' +
    '<kml xmlns="http://www.opengis.net/kml/2.2">' +
    `<Document>${placemarks}</Document>` +
    '</kml>'
  );
}
